import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { 
  Timer, TrendingUp, TrendingDown, Loader2, RefreshCw, Settings, 
  AlertTriangle, CheckCircle, XCircle, Clock, Calendar, Save,
  FileText, Users, Bell, Info, PlusCircle, MinusCircle, History, Edit3
} from 'lucide-react';
import { toast } from 'sonner';

const BancoHorasPage = () => {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('saldos');
  const [bancoHoras, setBancoHoras] = useState([]);
  const [empregados, setEmpregados] = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [ajustesManuais, setAjustesManuais] = useState([]);
  const [configuracao, setConfiguracao] = useState(null);
  const [sindicatos, setSindicatos] = useState([]);
  const [configsExistentes, setConfigsExistentes] = useState([]);
  const [escopoConfig, setEscopoConfig] = useState('geral');
  const [loading, setLoading] = useState(true);
  const [consolidating, setConsolidating] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Modal states
  const [showSolicitacaoModal, setShowSolicitacaoModal] = useState(false);
  const [showAjusteModal, setShowAjusteModal] = useState(false);
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [selectedEmpregadoHistorico, setSelectedEmpregadoHistorico] = useState(null);
  const [historicoAjustes, setHistoricoAjustes] = useState([]);
  const [observacao, setObservacao] = useState('');
  const [submittingAjuste, setSubmittingAjuste] = useState(false);
  
  // Ajuste manual form
  const [ajusteForm, setAjusteForm] = useState({
    empregado_id: '',
    tipo: 'credito',
    horas: '',
    minutos: '',
    justificativa: '',
    data_referencia: new Date().toISOString().split('T')[0],
    observacoes: ''
  });
  
  // New solicitation form
  const [novaSolicitacao, setNovaSolicitacao] = useState({
    empregado_id: '',
    data_compensacao: '',
    horas_solicitadas: '',
    motivo: ''
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchBancoHoras(),
      fetchEmpregados(),
      fetchSolicitacoes(),
      fetchAlertas(),
      fetchConfiguracao(),
      fetchSindicatosConfig(),
      fetchAjustesManuais()
    ]);
    setLoading(false);
  };

  const fetchBancoHoras = async () => {
    try {
      const response = await api.get('/banco-horas');
      setBancoHoras(response.data);
    } catch (error) {
      console.error('Error fetching banco horas:', error);
    }
  };

  const fetchEmpregados = async () => {
    try {
      const response = await api.get('/empregados');
      setEmpregados(response.data);
    } catch (error) {
      console.error('Error fetching empregados:', error);
    }
  };

  const fetchSolicitacoes = async () => {
    try {
      const response = await api.get(`/banco-horas/solicitacoes?empresa_id=${user?.empresa_id}`);
      setSolicitacoes(response.data);
    } catch (error) {
      console.error('Error fetching solicitacoes:', error);
    }
  };

  const fetchAjustesManuais = async () => {
    try {
      const response = await api.get('/banco-horas/ajustes-manuais');
      setAjustesManuais(response.data.ajustes || []);
    } catch (error) {
      console.error('Error fetching ajustes manuais:', error);
    }
  };

  const fetchHistoricoAjustes = async (empregadoId) => {
    try {
      const response = await api.get(`/banco-horas/ajustes-manuais/${empregadoId}/historico`);
      setHistoricoAjustes(response.data);
    } catch (error) {
      console.error('Error fetching historico ajustes:', error);
      toast.error('Erro ao carregar histórico de ajustes');
    }
  };

  const handleSubmitAjuste = async () => {
    // Validações
    if (!ajusteForm.empregado_id) {
      toast.error('Selecione um funcionário');
      return;
    }
    
    const horas = parseInt(ajusteForm.horas) || 0;
    const minutos = parseInt(ajusteForm.minutos) || 0;
    const totalMinutos = (horas * 60) + minutos;
    
    if (totalMinutos <= 0) {
      toast.error('Informe a quantidade de horas/minutos');
      return;
    }
    
    if (!ajusteForm.justificativa || ajusteForm.justificativa.length < 10) {
      toast.error('Justificativa deve ter pelo menos 10 caracteres');
      return;
    }
    
    setSubmittingAjuste(true);
    
    try {
      await api.post('/banco-horas/ajuste-manual', {
        empregado_id: ajusteForm.empregado_id,
        tipo: ajusteForm.tipo,
        minutos: totalMinutos,
        justificativa: ajusteForm.justificativa,
        data_referencia: ajusteForm.data_referencia,
        observacoes: ajusteForm.observacoes || null
      });
      
      const tipoTexto = ajusteForm.tipo === 'credito' ? 'Crédito' : 'Débito';
      toast.success(`${tipoTexto} de ${horas}h${minutos > 0 ? minutos + 'min' : ''} registrado com sucesso`);
      
      setShowAjusteModal(false);
      setAjusteForm({
        empregado_id: '',
        tipo: 'credito',
        horas: '',
        minutos: '',
        justificativa: '',
        data_referencia: new Date().toISOString().split('T')[0],
        observacoes: ''
      });
      
      // Recarregar dados
      await Promise.all([fetchBancoHoras(), fetchAjustesManuais()]);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar ajuste');
    } finally {
      setSubmittingAjuste(false);
    }
  };

  const openHistorico = async (empregado) => {
    setSelectedEmpregadoHistorico(empregado);
    await fetchHistoricoAjustes(empregado.empregado_id || empregado.id);
    setShowHistoricoModal(true);
  };

  const formatMinutosParaHoras = (minutos) => {
    const horas = Math.floor(Math.abs(minutos) / 60);
    const mins = Math.abs(minutos) % 60;
    const sinal = minutos < 0 ? '-' : '+';
    return `${sinal}${horas}h${mins > 0 ? mins + 'min' : ''}`;
  };

  const fetchAlertas = async () => {
    try {
      const response = await api.get(`/banco-horas/alertas?empresa_id=${user?.empresa_id}`);
      setAlertas(response.data);
    } catch (error) {
      console.error('Error fetching alertas:', error);
    }
  };

  const fetchSindicatosConfig = async () => {
    try {
      const [sind, cfgs] = await Promise.all([
        api.get(`/sindicatos?empresa_id=${user?.empresa_id}`),
        api.get(`/banco-horas/configuracoes?empresa_id=${user?.empresa_id}`),
      ]);
      setSindicatos(sind.data || []);
      setConfigsExistentes(cfgs.data || []);
    } catch (error) {
      console.error('Error fetching sindicatos:', error);
    }
  };

  const removerConfigSindicato = async () => {
    if (!window.confirm('Remover a configuração própria deste sindicato? Os empregados passam a usar a Geral.')) return;
    try {
      await api.delete(`/banco-horas/configuracao?empresa_id=${user?.empresa_id}&sindicato_id=${escopoConfig}`);
      toast.success('Configuração do sindicato removida');
      await fetchSindicatosConfig();
      fetchConfiguracao(escopoConfig);
    } catch (error) {
      toast.error('Erro ao remover configuração');
    }
  };

  const fetchConfiguracao = async (escopo = escopoConfig) => {
    try {
      const sind = escopo !== 'geral' ? `&sindicato_id=${escopo}` : '';
      const response = await api.get(`/banco-horas/configuracao?empresa_id=${user?.empresa_id}${sind}`);
      setConfiguracao(response.data);
    } catch (error) {
      console.error('Error fetching configuracao:', error);
      // Set default config
      setConfiguracao({
        empresa_id: user?.empresa_id,
        data_inicio_vigencia: '',
        data_fim_vigencia: '',
        prazo_vencimento_meses: 6,
        limite_maximo_positivo: 120,
        limite_maximo_negativo: 40,
        limite_he_normal_banco: 2,
        limite_he_folga_banco: 0,
        limite_he_feriado_banco: 0,
        percentual_hora_extra_normal: 50,
        percentual_hora_extra_sabado: 50,
        percentual_hora_extra_domingo: 100,
        percentual_hora_extra_feriado: 100,
        compensacao_proporcional: true,
        permitir_compensacao_parcial: true,
        alerta_vencimento_dias: 30,
        alerta_limite_percentual: 80,
        ativa: true
      });
    }
  };

  const consolidar = async () => {
    setConsolidating(true);
    const now = new Date();
    try {
      await api.post(`/banco-horas/consolidar?empresa_id=${user?.empresa_id}&mes=${now.getMonth() + 1}&ano=${now.getFullYear()}`);
      toast.success('Banco de horas consolidado com sucesso!');
      fetchBancoHoras();
      fetchAlertas();
    } catch (error) {
      toast.error('Erro ao consolidar banco de horas');
    } finally {
      setConsolidating(false);
    }
  };

  const salvarConfiguracao = async () => {
    setSavingConfig(true);
    try {
      await api.post('/banco-horas/configuracao', {
        ...configuracao,
        empresa_id: user?.empresa_id,
        sindicato_id: escopoConfig === 'geral' ? null : escopoConfig
      });
      toast.success('Configuração salva com sucesso!');
      fetchSindicatosConfig();
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  const criarSolicitacao = async () => {
    if (!novaSolicitacao.empregado_id || !novaSolicitacao.data_compensacao || !novaSolicitacao.horas_solicitadas) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      await api.post('/banco-horas/solicitacoes', {
        empresa_id: user?.empresa_id,
        ...novaSolicitacao,
        horas_solicitadas: parseFloat(novaSolicitacao.horas_solicitadas)
      });
      toast.success('Solicitação criada com sucesso!');
      setNovaSolicitacao({ empregado_id: '', data_compensacao: '', horas_solicitadas: '', motivo: '' });
      fetchSolicitacoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar solicitação');
    }
  };

  const aprovarSolicitacao = async () => {
    if (!selectedSolicitacao) return;
    try {
      await api.put(`/banco-horas/solicitacoes/${selectedSolicitacao.id}/aprovar?observacao=${encodeURIComponent(observacao)}`);
      toast.success('Solicitação aprovada!');
      setShowSolicitacaoModal(false);
      setSelectedSolicitacao(null);
      setObservacao('');
      fetchSolicitacoes();
    } catch (error) {
      toast.error('Erro ao aprovar solicitação');
    }
  };

  const rejeitarSolicitacao = async () => {
    if (!selectedSolicitacao) return;
    try {
      await api.put(`/banco-horas/solicitacoes/${selectedSolicitacao.id}/rejeitar?observacao=${encodeURIComponent(observacao)}`);
      toast.success('Solicitação rejeitada');
      setShowSolicitacaoModal(false);
      setSelectedSolicitacao(null);
      setObservacao('');
      fetchSolicitacoes();
    } catch (error) {
      toast.error('Erro ao rejeitar solicitação');
    }
  };

  const getEmpregadoNome = (id) => {
    const emp = empregados.find(e => e.id === id);
    return emp ? emp.nome : 'N/A';
  };

  const getSaldoDisplay = (saldo) => {
    const isPositive = saldo >= 0;
    return (
      <div className={`flex items-center gap-1 font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
        {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        {isPositive ? '+' : ''}{saldo.toFixed(1)}h
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const configs = {
      pendente: { variant: 'secondary', label: 'Pendente', icon: Clock },
      aprovada: { variant: 'default', label: 'Aprovada', icon: CheckCircle, className: 'bg-emerald-500' },
      rejeitada: { variant: 'destructive', label: 'Rejeitada', icon: XCircle },
      cancelada: { variant: 'outline', label: 'Cancelada', icon: XCircle }
    };
    const config = configs[status] || configs.pendente;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Group by employee for summary
  const summaryByEmployee = {};
  bancoHoras.forEach(bh => {
    if (!summaryByEmployee[bh.empregado_id]) {
      summaryByEmployee[bh.empregado_id] = {
        empregado_id: bh.empregado_id,
        total_creditos: 0,
        total_debitos: 0,
        saldo_atual: 0
      };
    }
    summaryByEmployee[bh.empregado_id].total_creditos += bh.creditos;
    summaryByEmployee[bh.empregado_id].total_debitos += bh.debitos;
    summaryByEmployee[bh.empregado_id].saldo_atual = bh.saldo_atual;
  });

  const summaryList = Object.values(summaryByEmployee);
  const totalCreditos = summaryList.reduce((acc, s) => acc + s.total_creditos, 0);
  const totalDebitos = summaryList.reduce((acc, s) => acc + s.total_debitos, 0);
  const totalSaldo = summaryList.reduce((acc, s) => acc + s.saldo_atual, 0);

  const solicitacoesPendentes = solicitacoes.filter(s => s.status === 'pendente');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="banco-horas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 font-['Outfit'] mb-1">Banco de Horas</h2>
          <p className="text-slate-500">Controle de créditos, débitos e compensações</p>
        </div>
        <div className="flex gap-2">
          {alertas.length > 0 && (
            <Badge variant="destructive" className="self-center">
              <Bell className="w-3 h-3 mr-1" />
              {alertas.length} alertas
            </Badge>
          )}
          <Button 
            onClick={consolidar} 
            disabled={consolidating}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="consolidar-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${consolidating ? 'animate-spin' : ''}`} />
            {consolidating ? 'Consolidando...' : 'Consolidar Mês'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Total Créditos</p>
                <p className="text-2xl font-bold text-emerald-600">+{totalCreditos.toFixed(1)}h</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Total Débitos</p>
                <p className="text-2xl font-bold text-red-600">-{totalDebitos.toFixed(1)}h</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Saldo Total</p>
                <p className={`text-2xl font-bold ${totalSaldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totalSaldo >= 0 ? '+' : ''}{totalSaldo.toFixed(1)}h
                </p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Timer className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Solicitações Pendentes</p>
                <p className="text-2xl font-bold text-amber-600">{solicitacoesPendentes.length}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 max-w-3xl">
          <TabsTrigger value="saldos" className="flex items-center gap-2" data-testid="tab-saldos">
            <Users className="w-4 h-4" />
            Saldos
          </TabsTrigger>
          <TabsTrigger value="ajustes" className="flex items-center gap-2" data-testid="tab-ajustes">
            <Edit3 className="w-4 h-4" />
            Ajustes
            {ajustesManuais.length > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                {ajustesManuais.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="solicitacoes" className="flex items-center gap-2" data-testid="tab-solicitacoes">
            <FileText className="w-4 h-4" />
            Solicitações
            {solicitacoesPendentes.length > 0 && (
              <Badge variant="destructive" className="ml-1 px-1.5 py-0 text-xs">
                {solicitacoesPendentes.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="alertas" className="flex items-center gap-2" data-testid="tab-alertas">
            <Bell className="w-4 h-4" />
            Alertas
            {alertas.length > 0 && (
              <Badge variant="destructive" className="ml-1 px-1.5 py-0 text-xs">
                {alertas.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="configuracao" className="flex items-center gap-2" data-testid="tab-configuracao">
            <Settings className="w-4 h-4" />
            Config
          </TabsTrigger>
        </TabsList>

        {/* Tab: Saldos */}
        <TabsContent value="saldos" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-['Outfit']">Saldo por Empregado</CardTitle>
                <CardDescription>Visão geral do banco de horas de cada empregado</CardDescription>
              </div>
              <Button 
                onClick={() => setShowAjusteModal(true)} 
                className="bg-accent hover:bg-accent/90"
                data-testid="btn-novo-ajuste"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Ajuste Manual
              </Button>
            </CardHeader>
            <CardContent>
              {summaryList.length === 0 ? (
                <div className="text-center py-12">
                  <Timer className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p className="text-slate-500">Nenhum registro de banco de horas</p>
                  <p className="text-sm text-slate-400 mt-1">Clique em "Consolidar Mês" para processar</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Empregado</TableHead>
                        <TableHead className="text-right">Créditos</TableHead>
                        <TableHead className="text-right">Débitos</TableHead>
                        <TableHead className="text-right">Saldo Atual</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summaryList.map((item) => (
                        <TableRow key={item.empregado_id}>
                          <TableCell className="font-medium">{getEmpregadoNome(item.empregado_id)}</TableCell>
                          <TableCell className="text-right text-emerald-600">+{item.total_creditos.toFixed(1)}h</TableCell>
                          <TableCell className="text-right text-red-600">-{item.total_debitos.toFixed(1)}h</TableCell>
                          <TableCell className="text-right">{getSaldoDisplay(item.saldo_atual)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Solicitações */}
        <TabsContent value="solicitacoes" className="mt-6 space-y-6">
          {/* Nova Solicitação */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Nova Solicitação de Compensação</CardTitle>
              <CardDescription>Solicite compensação de horas do banco</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Empregado *</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                    value={novaSolicitacao.empregado_id}
                    onChange={(e) => setNovaSolicitacao({...novaSolicitacao, empregado_id: e.target.value})}
                    data-testid="select-empregado-solicitacao"
                  >
                    <option value="">Selecione...</option>
                    {empregados.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Data Compensação *</Label>
                  <Input
                    type="date"
                    value={novaSolicitacao.data_compensacao}
                    onChange={(e) => setNovaSolicitacao({...novaSolicitacao, data_compensacao: e.target.value})}
                    data-testid="input-data-compensacao"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horas *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    placeholder="Ex: 4"
                    value={novaSolicitacao.horas_solicitadas}
                    onChange={(e) => setNovaSolicitacao({...novaSolicitacao, horas_solicitadas: e.target.value})}
                    data-testid="input-horas-solicitadas"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Motivo</Label>
                  <Input
                    placeholder="Motivo da compensação"
                    value={novaSolicitacao.motivo}
                    onChange={(e) => setNovaSolicitacao({...novaSolicitacao, motivo: e.target.value})}
                    data-testid="input-motivo"
                  />
                </div>
              </div>
              <Button 
                className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                onClick={criarSolicitacao}
                data-testid="btn-criar-solicitacao"
              >
                Criar Solicitação
              </Button>
            </CardContent>
          </Card>

          {/* Lista de Solicitações */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Solicitações de Compensação</CardTitle>
              <CardDescription>Gerencie as solicitações de compensação de horas</CardDescription>
            </CardHeader>
            <CardContent>
              {solicitacoes.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p className="text-slate-500">Nenhuma solicitação encontrada</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Empregado</TableHead>
                        <TableHead>Data Solicitação</TableHead>
                        <TableHead>Data Compensação</TableHead>
                        <TableHead className="text-right">Horas</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {solicitacoes.map((sol) => (
                        <TableRow key={sol.id}>
                          <TableCell className="font-medium">{getEmpregadoNome(sol.empregado_id)}</TableCell>
                          <TableCell>{sol.data_solicitacao}</TableCell>
                          <TableCell>{sol.data_compensacao}</TableCell>
                          <TableCell className="text-right font-semibold">{sol.horas_solicitadas}h</TableCell>
                          <TableCell className="max-w-[200px] truncate">{sol.motivo || '-'}</TableCell>
                          <TableCell>{getStatusBadge(sol.status)}</TableCell>
                          <TableCell className="text-right">
                            {sol.status === 'pendente' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedSolicitacao(sol);
                                  setShowSolicitacaoModal(true);
                                }}
                                data-testid={`btn-analisar-${sol.id}`}
                              >
                                Analisar
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Alertas */}
        <TabsContent value="alertas" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Alertas do Banco de Horas</CardTitle>
              <CardDescription>Notificações sobre vencimentos e limites</CardDescription>
            </CardHeader>
            <CardContent>
              {alertas.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p className="text-slate-500">Nenhum alerta no momento</p>
                  <p className="text-sm text-slate-400 mt-1">Tudo está dentro dos limites configurados</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alertas.map((alerta, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-lg border flex items-start gap-3 ${
                        alerta.tipo === 'vencimento' ? 'bg-amber-50 border-amber-200' :
                        alerta.tipo === 'limite_positivo' ? 'bg-blue-50 border-blue-200' :
                        alerta.tipo === 'limite_negativo' ? 'bg-red-50 border-red-200' :
                        'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                        alerta.tipo === 'vencimento' ? 'text-amber-600' :
                        alerta.tipo === 'limite_positivo' ? 'text-blue-600' :
                        alerta.tipo === 'limite_negativo' ? 'text-red-600' :
                        'text-slate-600'
                      }`} />
                      <div>
                        <p className="font-medium text-slate-800">{alerta.empregado_nome || 'Alerta'}</p>
                        <p className="text-sm text-slate-600">{alerta.mensagem}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Configuração */}
        <TabsContent value="configuracao" className="mt-6">
          <Card className="border-slate-200 mb-6" data-testid="bh-escopo-config-card">
            <CardContent className="pt-6 flex flex-col md:flex-row md:items-end gap-4">
              <div className="space-y-2 md:w-96">
                <Label>Aplicar configuração a</Label>
                <Select value={escopoConfig} onValueChange={(v) => { setEscopoConfig(v); fetchConfiguracao(v); }}>
                  <SelectTrigger data-testid="bh-escopo-config-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="geral" data-testid="bh-escopo-geral">Geral da empresa</SelectItem>
                    {sindicatos.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`bh-escopo-sindicato-${s.id}`}>
                        {s.nome}{configsExistentes.some((c) => c.sindicato_id === s.id) ? ' (configurado)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-slate-500 flex items-start gap-1.5" data-testid="bh-escopo-config-info">
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                {configuracao?.herdada_da_geral
                  ? 'Este sindicato ainda não tem configuração própria: os valores abaixo vêm da Geral. Salve para criar uma configuração própria.'
                  : 'Empregados de um sindicato com configuração própria usam a dele; os demais usam a Geral. Horas extras entram no banco sempre 1 para 1.'}
              </p>
              {escopoConfig !== 'geral' && configsExistentes.some((c) => c.sindicato_id === escopoConfig) && (
                <Button variant="outline" className="text-red-600 md:ml-auto" onClick={removerConfigSindicato} data-testid="bh-remover-config-sindicato-btn">
                  Remover configuração do sindicato
                </Button>
              )}
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Período de Vigência */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Período de Vigência do Banco de Horas
                </CardTitle>
                <CardDescription>Define o período em que o banco de horas está válido. Fora deste período, as HE serão pagas normalmente.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div className="space-y-2">
                    <Label>Data Início Vigência</Label>
                    <Input
                      type="text"
                      placeholder="DD/MM/AAAA"
                      value={configuracao?.data_inicio_vigencia || ''}
                      onChange={(e) => {
                        let val = e.target.value.replace(/\D/g, '');
                        if (val.length >= 2) val = val.slice(0,2) + '/' + val.slice(2);
                        if (val.length >= 5) val = val.slice(0,5) + '/' + val.slice(5,9);
                        setConfiguracao({...configuracao, data_inicio_vigencia: val});
                      }}
                      maxLength={10}
                      data-testid="input-data-inicio-vigencia"
                    />
                    <p className="text-xs text-slate-500">Deixe vazio para não ter limite inicial</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Data Fim Vigência</Label>
                    <Input
                      type="text"
                      placeholder="DD/MM/AAAA"
                      value={configuracao?.data_fim_vigencia || ''}
                      onChange={(e) => {
                        let val = e.target.value.replace(/\D/g, '');
                        if (val.length >= 2) val = val.slice(0,2) + '/' + val.slice(2);
                        if (val.length >= 5) val = val.slice(0,5) + '/' + val.slice(5,9);
                        setConfiguracao({...configuracao, data_fim_vigencia: val});
                      }}
                      maxLength={10}
                      data-testid="input-data-fim-vigencia"
                    />
                    <p className="text-xs text-slate-500">Deixe vazio para não ter limite final</p>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 p-3 rounded-lg">
                    <input
                      type="checkbox"
                      id="bh-ativa"
                      checked={configuracao?.ativa !== false}
                      onChange={(e) => setConfiguracao({...configuracao, ativa: e.target.checked})}
                      className="h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                      data-testid="checkbox-bh-ativa"
                    />
                    <Label htmlFor="bh-ativa" className="cursor-pointer font-medium text-blue-800">
                      Banco de Horas Ativo
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Prazos e Limites */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Prazos e Limites
                </CardTitle>
                <CardDescription>Configure os limites do banco de horas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prazo Vencimento (meses)</Label>
                    <Input
                      type="number"
                      value={configuracao?.prazo_vencimento_meses || 6}
                      onChange={(e) => setConfiguracao({...configuracao, prazo_vencimento_meses: parseInt(e.target.value)})}
                      data-testid="input-prazo-vencimento"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Alerta Vencimento (dias)</Label>
                    <Input
                      type="number"
                      value={configuracao?.alerta_vencimento_dias || 30}
                      onChange={(e) => setConfiguracao({...configuracao, alerta_vencimento_dias: parseInt(e.target.value)})}
                      data-testid="input-alerta-vencimento"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Limite Máximo Positivo (h)</Label>
                    <Input
                      type="number"
                      value={configuracao?.limite_maximo_positivo || 120}
                      onChange={(e) => setConfiguracao({...configuracao, limite_maximo_positivo: parseFloat(e.target.value)})}
                      data-testid="input-limite-positivo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Limite Máximo Negativo (h)</Label>
                    <Input
                      type="number"
                      value={configuracao?.limite_maximo_negativo || 40}
                      onChange={(e) => setConfiguracao({...configuracao, limite_maximo_negativo: parseFloat(e.target.value)})}
                      data-testid="input-limite-negativo"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Alerta ao atingir % do limite</Label>
                  <Input
                    type="number"
                    value={configuracao?.alerta_limite_percentual || 80}
                    onChange={(e) => setConfiguracao({...configuracao, alerta_limite_percentual: parseFloat(e.target.value)})}
                    data-testid="input-alerta-limite"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Limite Diário para Banco - Por Tipo de Dia */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Limite de HE para Banco por Tipo de Dia
                </CardTitle>
                <CardDescription>Defina quantas horas extras vão para o banco por tipo de dia (0 = todas pagas)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                      Dia Normal
                    </Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={configuracao?.limite_he_normal_banco ?? configuracao?.limite_horas_extras_diarias_banco ?? 2}
                      onChange={(e) => setConfiguracao({...configuracao, limite_he_normal_banco: parseFloat(e.target.value)})}
                      data-testid="input-limite-he-normal"
                    />
                    <p className="text-xs text-slate-500">Horas extras em dias úteis</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                      Folga Trabalhada
                    </Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={configuracao?.limite_he_folga_banco ?? 0}
                      onChange={(e) => setConfiguracao({...configuracao, limite_he_folga_banco: parseFloat(e.target.value)})}
                      data-testid="input-limite-he-folga"
                    />
                    <p className="text-xs text-slate-500">Trabalho em dia de folga</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-red-500"></span>
                      Feriado Trabalhado
                    </Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={configuracao?.limite_he_feriado_banco ?? 0}
                      onChange={(e) => setConfiguracao({...configuracao, limite_he_feriado_banco: parseFloat(e.target.value)})}
                      data-testid="input-limite-he-feriado"
                    />
                    <p className="text-xs text-slate-500">Trabalho em feriados</p>
                  </div>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">Como funciona?</p>
                      <ul className="space-y-1 list-disc ml-4">
                        <li><strong>Dia Normal:</strong> Até {configuracao?.limite_he_normal_banco ?? 2}h vão pro banco, excedente é pago</li>
                        <li><strong>Folga:</strong> {(configuracao?.limite_he_folga_banco ?? 0) === 0 ? 'Todas as horas são pagas (100%)' : `Até ${configuracao?.limite_he_folga_banco}h vão pro banco`}</li>
                        <li><strong>Feriado:</strong> {(configuracao?.limite_he_feriado_banco ?? 0) === 0 ? 'Todas as horas são pagas (100%)' : `Até ${configuracao?.limite_he_feriado_banco}h vão pro banco`}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tolerância de Entrada */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Tolerância de Entrada Antecipada
                </CardTitle>
                <CardDescription>Minutos antes do horário que gera ocorrência</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Tolerância (minutos)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="60"
                    value={configuracao?.tolerancia_entrada_antecipada ?? 5}
                    onChange={(e) => setConfiguracao({...configuracao, tolerancia_entrada_antecipada: parseInt(e.target.value)})}
                    data-testid="input-tolerancia-entrada"
                  />
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <strong>Exemplo:</strong> Se o horário de entrada é 08:00 e a tolerância é {configuracao?.tolerancia_entrada_antecipada ?? 5} minutos, 
                    entradas antes de {`0${8 - Math.floor((configuracao?.tolerancia_entrada_antecipada ?? 5) / 60)}`.slice(-2)}:{`0${60 - ((configuracao?.tolerancia_entrada_antecipada ?? 5) % 60)}`.slice(-2) === '60' ? '00' : `0${60 - ((configuracao?.tolerancia_entrada_antecipada ?? 5) % 60)}`.slice(-2)} 
                    gerarão ocorrência de "Entrada Antecipada".
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Regras de Compensação */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Regras de Compensação
                </CardTitle>
                <CardDescription>Configure as regras para compensação de horas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Compensação Parcial</p>
                      <p className="text-sm text-slate-500">Permite compensar parte das horas acumuladas</p>
                    </div>
                    <Switch
                      checked={configuracao?.permitir_compensacao_parcial || false}
                      onCheckedChange={(checked) => setConfiguracao({...configuracao, permitir_compensacao_parcial: checked})}
                      data-testid="switch-compensacao-parcial"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={salvarConfiguracao}
                    disabled={savingConfig}
                    data-testid="btn-salvar-configuracao"
                  >
                    {savingConfig ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {savingConfig ? 'Salvando...' : 'Salvar Configuração'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Ajustes Manuais */}
        <TabsContent value="ajustes" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-['Outfit']">Ajustes Manuais</CardTitle>
                <CardDescription>Histórico de créditos e débitos manuais no banco de horas</CardDescription>
              </div>
              <Button 
                onClick={() => setShowAjusteModal(true)} 
                className="bg-accent hover:bg-accent/90"
                data-testid="btn-novo-ajuste-tab"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Novo Ajuste
              </Button>
            </CardHeader>
            <CardContent>
              {ajustesManuais.length === 0 ? (
                <div className="text-center py-12">
                  <Edit3 className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p className="text-slate-500">Nenhum ajuste manual registrado</p>
                  <p className="text-sm text-slate-400 mt-1">Clique em "Novo Ajuste" para adicionar</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data Ref.</TableHead>
                      <TableHead>Funcionário</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Quantidade</TableHead>
                      <TableHead>Justificativa</TableHead>
                      <TableHead>Autor</TableHead>
                      <TableHead>Registrado em</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ajustesManuais.map((ajuste) => (
                      <TableRow key={ajuste.id}>
                        <TableCell className="font-mono text-sm">
                          {ajuste.data_referencia}
                        </TableCell>
                        <TableCell className="font-medium">
                          {ajuste.empregado_nome || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={ajuste.tipo === 'credito' ? 'default' : 'destructive'}
                            className={ajuste.tipo === 'credito' ? 'bg-emerald-500' : ''}
                          >
                            {ajuste.tipo === 'credito' ? (
                              <><PlusCircle className="w-3 h-3 mr-1" /> Crédito</>
                            ) : (
                              <><MinusCircle className="w-3 h-3 mr-1" /> Débito</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">
                          {formatMinutosParaHoras(ajuste.tipo === 'credito' ? ajuste.minutos : -ajuste.minutos)}
                        </TableCell>
                        <TableCell className="max-w-xs truncate" title={ajuste.justificativa}>
                          {ajuste.justificativa}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {ajuste.autor_nome}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(ajuste.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Ajuste Manual */}
      <Dialog open={showAjusteModal} onOpenChange={setShowAjusteModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5" />
              Ajuste Manual do Banco de Horas
            </DialogTitle>
            <DialogDescription>
              Adicione crédito ou débito manualmente no banco de horas de um funcionário
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Funcionário */}
            <div className="space-y-2">
              <Label>Funcionário *</Label>
              <Select
                value={ajusteForm.empregado_id}
                onValueChange={(value) => setAjusteForm({...ajusteForm, empregado_id: value})}
              >
                <SelectTrigger data-testid="select-empregado-ajuste">
                  <SelectValue placeholder="Selecione o funcionário" />
                </SelectTrigger>
                <SelectContent>
                  {empregados.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nome} {emp.matricula ? `(${emp.matricula})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tipo */}
            <div className="space-y-2">
              <Label>Tipo de Ajuste *</Label>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant={ajusteForm.tipo === 'credito' ? 'default' : 'outline'}
                  className={ajusteForm.tipo === 'credito' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                  onClick={() => setAjusteForm({...ajusteForm, tipo: 'credito'})}
                  data-testid="btn-tipo-credito"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Crédito (+)
                </Button>
                <Button
                  type="button"
                  variant={ajusteForm.tipo === 'debito' ? 'default' : 'outline'}
                  className={ajusteForm.tipo === 'debito' ? 'bg-red-500 hover:bg-red-600' : ''}
                  onClick={() => setAjusteForm({...ajusteForm, tipo: 'debito'})}
                  data-testid="btn-tipo-debito"
                >
                  <MinusCircle className="w-4 h-4 mr-2" />
                  Débito (-)
                </Button>
              </div>
            </div>

            {/* Quantidade */}
            <div className="space-y-2">
              <Label>Quantidade *</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    type="number"
                    min="0"
                    placeholder="Horas"
                    value={ajusteForm.horas}
                    onChange={(e) => setAjusteForm({...ajusteForm, horas: e.target.value})}
                    data-testid="input-horas-ajuste"
                  />
                  <p className="text-xs text-slate-500 mt-1">Horas</p>
                </div>
                <div className="flex-1">
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    placeholder="Minutos"
                    value={ajusteForm.minutos}
                    onChange={(e) => setAjusteForm({...ajusteForm, minutos: e.target.value})}
                    data-testid="input-minutos-ajuste"
                  />
                  <p className="text-xs text-slate-500 mt-1">Minutos</p>
                </div>
              </div>
            </div>

            {/* Data de Referência */}
            <div className="space-y-2">
              <Label>Data de Referência *</Label>
              <Input
                type="date"
                value={ajusteForm.data_referencia}
                onChange={(e) => setAjusteForm({...ajusteForm, data_referencia: e.target.value})}
                data-testid="input-data-ajuste"
              />
              <p className="text-xs text-slate-500">Data a que se refere o ajuste</p>
            </div>

            {/* Justificativa */}
            <div className="space-y-2">
              <Label>Justificativa * (mínimo 10 caracteres)</Label>
              <Textarea
                placeholder="Descreva o motivo do ajuste..."
                value={ajusteForm.justificativa}
                onChange={(e) => setAjusteForm({...ajusteForm, justificativa: e.target.value})}
                rows={3}
                data-testid="input-justificativa-ajuste"
              />
            </div>

            {/* Observações */}
            <div className="space-y-2">
              <Label>Observações (opcional)</Label>
              <Input
                placeholder="Observações adicionais..."
                value={ajusteForm.observacoes}
                onChange={(e) => setAjusteForm({...ajusteForm, observacoes: e.target.value})}
                data-testid="input-observacoes-ajuste"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAjusteModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmitAjuste} 
              disabled={submittingAjuste}
              className={ajusteForm.tipo === 'credito' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}
              data-testid="btn-confirmar-ajuste"
            >
              {submittingAjuste ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
              ) : (
                <>
                  {ajusteForm.tipo === 'credito' ? <PlusCircle className="w-4 h-4 mr-2" /> : <MinusCircle className="w-4 h-4 mr-2" />}
                  Confirmar {ajusteForm.tipo === 'credito' ? 'Crédito' : 'Débito'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Histórico de Ajustes */}
      <Dialog open={showHistoricoModal} onOpenChange={setShowHistoricoModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Histórico de Ajustes
            </DialogTitle>
            <DialogDescription>
              {selectedEmpregadoHistorico?.nome || historicoAjustes?.empregado_nome}
            </DialogDescription>
          </DialogHeader>
          
          {historicoAjustes && (
            <div className="space-y-4">
              {/* Resumo */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-slate-500">Total Créditos</p>
                  <p className="text-lg font-bold text-emerald-600">
                    {formatMinutosParaHoras(historicoAjustes.total_creditos_minutos || 0)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500">Total Débitos</p>
                  <p className="text-lg font-bold text-red-600">
                    {formatMinutosParaHoras(-(historicoAjustes.total_debitos_minutos || 0))}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500">Saldo Ajustes</p>
                  <p className={`text-lg font-bold ${(historicoAjustes.saldo_ajustes_minutos || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatMinutosParaHoras(historicoAjustes.saldo_ajustes_minutos || 0)}
                  </p>
                </div>
              </div>

              {/* Lista de Ajustes */}
              <div className="max-h-64 overflow-y-auto">
                {historicoAjustes.ajustes?.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nenhum ajuste registrado</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Qtd</TableHead>
                        <TableHead>Justificativa</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historicoAjustes.ajustes?.map((ajuste) => (
                        <TableRow key={ajuste.id}>
                          <TableCell className="text-sm">{ajuste.data_referencia}</TableCell>
                          <TableCell>
                            <Badge variant={ajuste.tipo === 'credito' ? 'default' : 'destructive'} className={ajuste.tipo === 'credito' ? 'bg-emerald-500' : ''}>
                              {ajuste.tipo === 'credito' ? '+' : '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {formatMinutosParaHoras(ajuste.tipo === 'credito' ? ajuste.minutos : -ajuste.minutos)}
                          </TableCell>
                          <TableCell className="text-sm max-w-xs truncate">{ajuste.justificativa}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowHistoricoModal(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Análise de Solicitação */}
      <Dialog open={showSolicitacaoModal} onOpenChange={setShowSolicitacaoModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Analisar Solicitação</DialogTitle>
            <DialogDescription>
              Aprove ou rejeite esta solicitação de compensação
            </DialogDescription>
          </DialogHeader>
          {selectedSolicitacao && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Empregado</p>
                  <p className="font-medium">{getEmpregadoNome(selectedSolicitacao.empregado_id)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Horas Solicitadas</p>
                  <p className="font-medium">{selectedSolicitacao.horas_solicitadas}h</p>
                </div>
                <div>
                  <p className="text-slate-500">Data Solicitação</p>
                  <p className="font-medium">{selectedSolicitacao.data_solicitacao}</p>
                </div>
                <div>
                  <p className="text-slate-500">Data Compensação</p>
                  <p className="font-medium">{selectedSolicitacao.data_compensacao}</p>
                </div>
              </div>
              {selectedSolicitacao.motivo && (
                <div>
                  <p className="text-slate-500 text-sm">Motivo</p>
                  <p className="font-medium">{selectedSolicitacao.motivo}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Observação (opcional)</Label>
                <Input
                  placeholder="Adicione uma observação..."
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  data-testid="input-observacao-solicitacao"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowSolicitacaoModal(false);
                setSelectedSolicitacao(null);
                setObservacao('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={rejeitarSolicitacao}
              data-testid="btn-rejeitar-solicitacao"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Rejeitar
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={aprovarSolicitacao}
              data-testid="btn-aprovar-solicitacao"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Aprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BancoHorasPage;
